import { lazy, Suspense, useContext, useEffect, useMemo } from "react"
import {
  Grid,
  Skeleton,
  Stack,
  TableCell,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import PageContainer from "../../../components/global/PageContainer"
import FadeFromTop from "../../../components/animations/FadeFromTop"
import Card from "../../../components/global/Card"
import Title from "../../../components/global/Title"
import Text from "../../../components/global/Text"
import Table from "../../../components/global/Table"
import TableRow from "../../../components/global/TableRow"
import LineChart from "../../../components/global/LineChart"
import LifestyleCard from "../../../components/footprint/LifestyleCard"
import MobilityDonutCard from "../../../components/mobility/MobilityDonutCard"
import { AuthContext } from "../../../controllers/auth"
import { MobilityContext } from "../../../controllers/mobility"
import { colors } from "../../../services/config/colors"
import { countriesAlphaCode } from "../../../services/config/countries"
import { formatNumber } from "../../../services/utils/utils"
import {
  formatMobilityNumber,
  getDayLabel,
  getTransportModeColor,
  getTransportModeLabel,
  getUsersMapColor,
  groupTopItems,
  MobilityChartItem,
} from "../../../services/utils/mobility"
import { MobilityTransportItem } from "../../../models/mobility.models"

// the map library and the country shapes are loaded only when the page is opened
const MobilityWorldMap = lazy(
  () => import("../../../components/mobility/MobilityWorldMap")
)

const MobilityAssistant = () => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const matchesXL = useMediaQuery(theme.breakpoints.up("xl"))
  const matchesMD = useMediaQuery(theme.breakpoints.up("md"))
  const { team } = useContext(AuthContext)
  const {
    mobilityAssistantDataLoading,
    mobilityTransportList,
    mobilityWeeklyList,
    mobilityCountriesList,
    mobilitySustainableSummary,
    getMobilityAssistantData,
  } = useContext(MobilityContext)

  // get data
  useEffect(() => {
    if (team) {
      getMobilityAssistantData()
    }
  }, [team])

  // sustainable means of transport, sorted by avoided co2
  const sustainableTransports = useMemo(
    () =>
      mobilityTransportList
        .filter((item) => item.sustainability === "sustainable")
        .sort((a, b) => (b.co2AvoidedKg ?? 0) - (a.co2AvoidedKg ?? 0)),
    [mobilityTransportList]
  )

  // donut charts data (top 4 means of transport + others)
  const getDonutItems = (
    field: keyof Pick<
      MobilityTransportItem,
      "durationMinutes" | "distanceKm" | "co2AvoidedKg"
    >
  ): MobilityChartItem[] =>
    groupTopItems(
      mobilityTransportList.map((item, index) => ({
        id: item.type,
        label: getTransportModeLabel(t, item.type),
        value: item[field] ?? 0,
        color: getTransportModeColor(item.type, index),
      })),
      4,
      t("others")
    )

  const durationDonutItems = useMemo(
    () => getDonutItems("durationMinutes"),
    [mobilityTransportList, i18n.language]
  )
  const distanceDonutItems = useMemo(
    () => getDonutItems("distanceKm"),
    [mobilityTransportList, i18n.language]
  )
  const co2AvoidedDonutItems = useMemo(
    () => getDonutItems("co2AvoidedKg"),
    [mobilityTransportList, i18n.language]
  )

  // users per country for the map, and users without a country (shown as a note)
  const mapItems = useMemo(
    () =>
      mobilityCountriesList
        .filter((item) => !!item.country)
        .map((item) => ({ country: item.country!, users: item.users ?? 0 })),
    [mobilityCountriesList]
  )

  const notSetUsers = mobilityCountriesList
    .filter((item) => !item.country)
    .reduce((sum, item) => sum + (item.users ?? 0), 0)

  // users per country for the list next to the map, most users first; unset countries last
  const countryRows = useMemo(() => {
    const names =
      (countriesAlphaCode as Record<string, Record<string, string>>)[
        i18n.language
      ] ?? (countriesAlphaCode as Record<string, Record<string, string>>).en
    const rows = [...mapItems]
      .sort((a, b) => b.users - a.users)
      .map((item) => ({
        label: names?.[item.country.toUpperCase()] ?? item.country,
        users: item.users,
        isSet: true,
      }))
    return notSetUsers > 0
      ? [
          ...rows,
          { label: t("country_not_set"), users: notSetUsers, isSet: false },
        ]
      : rows
  }, [mapItems, notSetUsers, i18n.language])

  // the list next to the map, styled as its legend: each country's map colour, name and users
  const maxCountryUsers = Math.max(0, ...mapItems.map((item) => item.users))
  const countryList = (
    <Stack
      gap={0.75}
      style={{ maxHeight: matchesMD ? 440 : "none", overflowY: "auto" }}
    >
      {countryRows.map((row) => (
        <Stack key={row.label} direction="row" alignItems="center" gap={1}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              flexShrink: 0,
              backgroundColor: row.isSet
                ? getUsersMapColor(
                    maxCountryUsers ? row.users / maxCountryUsers : 0
                  )
                : colors.chartsGrid,
            }}
          />
          <Text fontSize={12} fontWeight={500} style={{ flex: 1 }}>
            {row.label}
          </Text>
          <Text fontSize={12} fontWeight={500} color={colors.textSecondary}>
            {formatNumber(row.users)}
          </Text>
        </Stack>
      ))}
    </Stack>
  )

  // distinct users with a sustainable trip (the same total on every country row)
  const sustainableUsers = mobilityCountriesList[0]?.totalUsers ?? 0

  const donutWidth = matchesXL ? "calc(100% / 3 - 16px)" : "100%"

  return (
    <PageContainer
      title={t("mobility_assistant")}
      breadcrumbs={[{ title: t("mobility_assistant") }]}
      withFilters={false}
      infoText={t("mobility_assistant_info")}
    >
      {/* general data, at the top as in Looker: users on the left, a metric / value table on the right */}
      <FadeFromTop>
        <Card>
          <Title>{t("general_data")}</Title>
          <Grid container spacing={3} columns={{ xs: 1, md: 3 }}>
            <Grid item xs={1}>
              {/* users with at least one sustainable trip in the period */}
              <LifestyleCard
                title={t("users")}
                value={formatNumber(sustainableUsers)}
                loading={mobilityAssistantDataLoading}
              />
            </Grid>
            <Grid item xs={1} md={2}>
              <Table
                headingItems={[t("sustainable_transport_metrics"), t("value")]}
                loading={mobilityAssistantDataLoading}
                style={{ boxShadow: "none" }}
              >
                {[
                  {
                    title: t("total_avoided_co2e_kg"),
                    value: mobilitySustainableSummary?.totalCo2AvoidedKg,
                  },
                  {
                    title: t("total_distance_km"),
                    value: mobilitySustainableSummary?.totalDistanceKm,
                  },
                  {
                    title: t("total_time_min"),
                    value: mobilitySustainableSummary?.totalDurationMinutes,
                    decimalPlaces: 0,
                  },
                  {
                    title: t("average_avoided_co2e_per_user_kg"),
                    value: mobilitySustainableSummary?.averageCo2AvoidedKgPerUser,
                  },
                  {
                    title: t("average_distance_per_user_km"),
                    value: mobilitySustainableSummary?.averageDistanceKmPerUser,
                  },
                  {
                    title: t("average_time_per_user_min"),
                    value: mobilitySustainableSummary?.averageDurationMinutesPerUser,
                  },
                ].map((item, index) => (
                  <TableRow index={index} key={item.title}>
                    <TableCell>
                      <Text fontSize={14} fontWeight={500}>
                        {item.title}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text fontSize={14} fontWeight={500}>
                        {mobilitySustainableSummary
                          ? formatMobilityNumber(item.value, item.decimalPlaces)
                          : t("no_data")}
                      </Text>
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
            </Grid>
          </Grid>
        </Card>
      </FadeFromTop>
      {/* metrics per sustainable means of transport */}
      <FadeFromTop delay={0.05} style={{ marginTop: 24 }}>
        <Card>
          <Title>{t("metrics_per_sustainable_transport")}</Title>
          {!mobilityAssistantDataLoading && !sustainableTransports.length ? (
            <Text fontWeight={500} color={colors.textSecondary}>
              {t("no_data")}
            </Text>
          ) : (
            <Table
              headingItems={[
                t("mode_of_transport"),
                t("avoided_co2e_kg"),
                t("registered_distance_km"),
                t("time_spent_min"),
                t("number_of_trips"),
              ]}
              loading={mobilityAssistantDataLoading}
              style={{ boxShadow: "none" }}
            >
              {sustainableTransports.map((item, index) => (
                <TableRow index={index} key={item.type}>
                  <TableCell>
                    <Text fontSize={14} fontWeight={500}>
                      {getTransportModeLabel(t, item.type)}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text fontSize={14} fontWeight={500}>
                      {formatMobilityNumber(item.co2AvoidedKg)}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text fontSize={14} fontWeight={500}>
                      {formatMobilityNumber(item.distanceKm)}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text fontSize={14} fontWeight={500}>
                      {formatMobilityNumber(item.durationMinutes, 0)}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text fontSize={14} fontWeight={500}>
                      {formatNumber(item.trips)}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          )}
        </Card>
      </FadeFromTop>
      {/* donut charts per mode of transport */}
      <FadeFromTop delay={0.05} style={{ marginTop: 24 }}>
        <Stack direction={{ lg: "column", xl: "row" }} gap={3}>
          <MobilityDonutCard
            title={t("time_spent_per_transport")}
            items={durationDonutItems}
            unit={t("minutes_short")}
            loading={mobilityAssistantDataLoading}
            width={donutWidth}
          />
          <MobilityDonutCard
            title={t("distance_per_transport")}
            items={distanceDonutItems}
            unit="km"
            loading={mobilityAssistantDataLoading}
            width={donutWidth}
          />
          <MobilityDonutCard
            title={t("avoided_co2e_per_transport")}
            items={co2AvoidedDonutItems}
            unit={t("kg_co2e_avoided")}
            loading={mobilityAssistantDataLoading}
            width={donutWidth}
          />
        </Stack>
      </FadeFromTop>
      {/* cumulative avoided co2 by week */}
      <FadeFromTop delay={0.1} style={{ marginTop: 24 }}>
        <Card style={{ overflowX: "scroll" }}>
          <Stack gap={3} style={{ minWidth: 880 }}>
            <Title>{t("cumulated_sum")}</Title>
            {!mobilityAssistantDataLoading && !mobilityWeeklyList.length ? (
              <Text fontWeight={500} color={colors.textSecondary}>
                {t("no_data")}
              </Text>
            ) : (
              <>
                <Text
                  fontSize={12}
                  fontWeight={500}
                  color={colors.primary}
                  style={{ marginBottom: -16 }}
                >
                  {t("kg_co2e_avoided")}
                </Text>
                <div
                  style={{ width: "100%", height: 250, position: "relative" }}
                >
                  <LineChart
                    data={[
                      {
                        label: t("avoided_co2e"),
                        data: mobilityWeeklyList.map((item) => ({
                          label: getDayLabel(t, item.weekStart),
                          value: item.cumulativeCo2AvoidedKg ?? 0,
                          backgroundColor: colors.categories.episodes,
                        })),
                      },
                    ]}
                    loading={mobilityAssistantDataLoading}
                  />
                </div>
              </>
            )}
          </Stack>
        </Card>
      </FadeFromTop>
      {/* users per country */}
      <FadeFromTop delay={0.2} style={{ marginTop: 24 }}>
        <Card>
          <Title>{t("users_per_country")}</Title>
          <Stack direction="row" gap={3} alignItems="flex-end">
            <div
              style={{
                flex: 1,
                minWidth: 0,
                height: matchesMD ? 440 : 220,
                position: "relative",
              }}
            >
              <Suspense
                fallback={
                  <Skeleton
                    variant="rounded"
                    style={{ width: "100%", height: "100%" }}
                  />
                }
              >
                <MobilityWorldMap
                  items={mapItems}
                  loading={mobilityAssistantDataLoading}
                />
              </Suspense>
            </div>
            {/* users per country, as Looker's table next to the map */}
            {matchesMD && !mobilityAssistantDataLoading && countryRows.length ? (
              <div style={{ width: 220, flexShrink: 0 }}>{countryList}</div>
            ) : null}
          </Stack>
          {!matchesMD && !mobilityAssistantDataLoading && countryRows.length
            ? countryList
            : null}
        </Card>
      </FadeFromTop>
      {/* data update note */}
      <Text
        fontSize={14}
        fontWeight={500}
        color={colors.textSecondary}
        style={{ marginTop: 24 }}
      >
        {t("data_updated_near_real_time")}
      </Text>
    </PageContainer>
  )
}

export default MobilityAssistant
